import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Plus, Edit, RotateCcw, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { LiveStatusDot } from "../../components/LiveStatusDot";
import { useRikshawStream } from "../../hooks/useRikshawStream";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const TABS = [
  { id: "rikshaws", label: "Rikshaws" },
  { id: "drivers", label: "Drivers" },
];

export default function AdminFleet() {
  const [tab, setTab] = useState("rikshaws");

  return (
    <div className="space-y-5" data-testid="admin-fleet">
      <div>
        <span className="label-overline text-jow-gold">Operations</span>
        <h1 className="font-display text-3xl text-jow-navy mt-1">Fleet</h1>
      </div>
      <div className="inline-flex border border-jow-navy/15 bg-white">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest ${
              tab === t.id ? "bg-jow-navy text-jow-cream" : "text-jow-navy/60"
            }`}
            data-testid={`fleet-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "rikshaws" && <RikshawsTab />}
      {tab === "drivers" && <DriversTab />}
    </div>
  );
}

function RikshawsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/rikshaws").then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);

  useRikshawStream((evt) => {
    setItems((prev) =>
      prev.map((r) =>
        r.id === evt.rikshaw_id
          ? { ...r, lat: evt.lat, lng: evt.lng, status: evt.status, last_ping_at: evt.last_ping_at }
          : r
      )
    );
  });

  return (
    <div className="space-y-4" data-testid="fleet-rikshaws">
      <div className="flex justify-between items-center">
        <span className="text-xs text-jow-navy/55">{items.length} vehicles in fleet</span>
        <button
          onClick={() => setEditing({})}
          className="btn-primary-jow text-[11px]"
          data-testid="new-rikshaw-btn"
        >
          <Plus size={13} /> New rikshaw
        </button>
      </div>

      <div className="bg-white border border-jow-navy/10" style={{ height: 350 }}>
        <MapContainer
          center={[22.97, 78.65]}
          zoom={5}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {items.map((r) => (
            <Marker key={r.id} position={[r.lat, r.lng]} icon={icon}>
              <Popup>
                <strong>{r.code}</strong>
                <br />
                {r.driver?.name || r.driver_name}
                <br />
                {r.today_delivered} delivered today
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Code</th>
            <th className="text-left px-4 py-3">Driver</th>
            <th className="text-left px-4 py-3">City</th>
            <th className="text-center px-4 py-3">Status</th>
            <th className="text-center px-4 py-3">Live</th>
            <th className="text-right px-4 py-3">Orders today</th>
            <th className="text-right px-4 py-3">Delivered today</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-t border-jow-navy/5" data-testid={`rikshaw-${r.code}`}>
              <td className="px-4 py-3 font-display text-jow-navy">{r.code}</td>
              <td className="px-4 py-3 text-jow-navy/70">{r.driver?.name || r.driver_name}</td>
              <td className="px-4 py-3 text-jow-navy/70">{r.city}</td>
              <td className="px-4 py-3 text-center">
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-jow-navy/5 text-jow-navy/70">
                  {r.status}
                </span>
              </td>
              <td className="px-4 py-3 text-center">
                <LiveStatusDot lastPingAt={r.last_ping_at} testid={`fleet-dot-${r.code}`} />
              </td>
              <td className="px-4 py-3 text-right">{r.today_orders}</td>
              <td className="px-4 py-3 text-right">{r.today_delivered}</td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => setEditing(r)}
                  className="text-jow-pink"
                  data-testid={`rikshaw-edit-${r.code}`}
                >
                  <Edit size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <RikshawDrawer
          rikshaw={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function RikshawDrawer({ rikshaw, onClose, onSaved }) {
  const [form, setForm] = useState(
    rikshaw || { code: "", city: "", area: "", lat: 22, lng: 78, status: "idle" }
  );
  const save = async () => {
    try {
      if (rikshaw) {
        await api.patch(`/admin/rikshaws/${rikshaw.id}`, form);
      } else {
        await api.post("/admin/rikshaws", form);
      }
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };
  return (
    <div
      className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
      onClick={onClose}
      data-testid="rikshaw-drawer"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-jow-navy">
            {rikshaw ? `Edit ${rikshaw.code}` : "New rikshaw"}
          </h2>
          <button onClick={onClose} className="text-jow-navy/50">
            <X size={16} />
          </button>
        </div>
        {!rikshaw && (
          <FInput label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
        )}
        <FInput label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
        <FInput label="Area" value={form.area || ""} onChange={(v) => setForm({ ...form, area: v })} />
        <div className="grid grid-cols-2 gap-3">
          <FInput
            label="Latitude"
            type="number"
            value={form.lat}
            onChange={(v) => setForm({ ...form, lat: Number(v) })}
          />
          <FInput
            label="Longitude"
            type="number"
            value={form.lng}
            onChange={(v) => setForm({ ...form, lng: Number(v) })}
          />
        </div>
        <div>
          <label className="label-overline block mb-1">Status</label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
          >
            {["active", "idle", "on_delivery", "maintenance"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <button onClick={save} className="btn-primary-jow w-full" data-testid="rikshaw-save">
          Save
        </button>
      </aside>
    </div>
  );
}

function DriversTab() {
  const [items, setItems] = useState([]);
  const [rikshaws, setRikshaws] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () =>
    Promise.all([api.get("/admin/drivers"), api.get("/admin/rikshaws")]).then(([d, r]) => {
      setItems(d.data.items || []);
      setRikshaws(r.data.items || []);
    });
  useEffect(() => {
    load();
  }, []);

  const reset = async (d) => {
    if (!window.confirm(`Force re-login for ${d.name}?`)) return;
    await api.post(`/admin/drivers/${d.id}/reset`);
    toast.success(`${d.name}'s sessions invalidated`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="fleet-drivers">
      <div className="flex justify-between items-center">
        <span className="text-xs text-jow-navy/55">{items.length} drivers</span>
        <button
          onClick={() => setEditing({})}
          className="btn-primary-jow text-[11px]"
          data-testid="new-driver-btn"
        >
          <Plus size={13} /> Add driver
        </button>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">Phone</th>
            <th className="text-left px-4 py-3">Rikshaw</th>
            <th className="text-center px-4 py-3">Active</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id} className="border-t border-jow-navy/5" data-testid={`driver-${d.phone}`}>
              <td className="px-4 py-3 text-jow-navy">{d.name}</td>
              <td className="px-4 py-3 text-jow-navy/70">{d.phone}</td>
              <td className="px-4 py-3 text-jow-navy/70 font-mono text-xs">{d.rikshaw_id}</td>
              <td className="px-4 py-3 text-center">
                <span
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                    d.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  }`}
                >
                  {d.is_active ? "on" : "off"}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => setEditing(d)}
                  className="text-jow-pink mr-2"
                  data-testid={`driver-edit-${d.phone}`}
                >
                  <Edit size={13} />
                </button>
                <button
                  onClick={() => reset(d)}
                  className="text-jow-navy/50"
                  title="Force re-login"
                  data-testid={`driver-reset-${d.phone}`}
                >
                  <RotateCcw size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <DriverDrawer
          driver={editing.id ? editing : null}
          rikshaws={rikshaws}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function DriverDrawer({ driver, rikshaws, onClose, onSaved }) {
  const [form, setForm] = useState(
    driver || { name: "", phone: "", rikshaw_id: "", email: "", photo_url: "" }
  );
  const save = async () => {
    try {
      if (driver) {
        await api.patch(`/admin/drivers/${driver.id}`, form);
      } else {
        await api.post("/admin/drivers", form);
      }
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };
  return (
    <div
      className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
      onClick={onClose}
      data-testid="driver-drawer"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-jow-navy">
            {driver ? `Edit ${driver.name}` : "New driver"}
          </h2>
          <button onClick={onClose} className="text-jow-navy/50">
            <X size={16} />
          </button>
        </div>
        <FInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        {!driver && (
          <FInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        )}
        <FInput
          label="Email"
          value={form.email || ""}
          onChange={(v) => setForm({ ...form, email: v })}
        />
        <div>
          <label className="label-overline block mb-1">Rikshaw</label>
          <select
            value={form.rikshaw_id}
            onChange={(e) => setForm({ ...form, rikshaw_id: e.target.value })}
            className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
            data-testid="d-rikshaw"
          >
            <option value="">— pick —</option>
            {rikshaws.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} · {r.city}
              </option>
            ))}
          </select>
        </div>
        {driver && (
          <label className="text-sm flex items-center gap-2 text-jow-navy/70">
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        )}
        <button onClick={save} className="btn-primary-jow w-full" data-testid="d-save">
          Save
        </button>
      </aside>
    </div>
  );
}

function FInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
      />
    </div>
  );
}
